import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import AvatarCard from './AvatarCard';

function GachaAnimation({ open, onClose, result, onAnimationComplete, avatars = [] }) {
  const [animationStage, setAnimationStage] = useState('spinning'); // spinning, revealing, complete
  const [revealedAvatar, setRevealedAvatar] = useState(null);
  const [wheelAvatars, setWheelAvatars] = useState([]);
  const wheelRef = useRef(null);
  const targetIndexRef = useRef(-1);
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  const hasCompletedRef = useRef(false);
  
  // Update ref when callback changes
  useEffect(() => {
    onAnimationCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  // Prepare wheel avatars when result changes
  useEffect(() => {
    if (open && result && avatars.length > 0) {
      // Fixed target position - we'll always stop at index 50
      const FIXED_TARGET_INDEX = 50;
      targetIndexRef.current = FIXED_TARGET_INDEX;
      
      // Create a shuffled list of avatars (excluding result)
      const otherAvatars = avatars.filter(a => a.id !== result.avatar.id);
      const shuffled = [...otherAvatars].sort(() => Math.random() - 0.5);
      
      // Create array with fixed size, fill with random avatars
      const totalCards = 100; // Enough cards for smooth animation
      const finalList = [];
      
      for (let i = 0; i < totalCards; i++) {
        if (i === FIXED_TARGET_INDEX) {
          // Place result avatar at fixed position
          finalList.push(result.avatar);
        } else {
          // Fill other positions with random avatars
          finalList.push(shuffled[i % shuffled.length]);
        }
      }
      
      setWheelAvatars(finalList);
    }
  }, [open, result, avatars]);

  useEffect(() => {
    if (open && result && !hasCompletedRef.current && wheelAvatars.length > 0 && wheelRef.current) {
      // Reset completion flag
      hasCompletedRef.current = false;
      
      // Start spinning animation
      setAnimationStage('spinning');
      setRevealedAvatar(null);
      
      const wheelElement = wheelRef.current;
      if (!wheelElement) return;
      
      let animationFrameId = null;
      let revealTimeout = null;
      let completeTimeout = null;
      
      // Wait for next frame to ensure element is rendered
      const startAnimation = () => {
        // Get actual measurements from DOM
        const firstCard = wheelElement.firstElementChild;
        if (!firstCard) {
          console.error('No cards found in wheel');
          return;
        }
        
        // Calculate actual card width including gap by measuring DOM
        const cardWidth = firstCard.offsetWidth;
        
        // Measure gap by checking distance between first two cards
        const secondCard = wheelElement.children[1];
        let gap = 16; // Default
        if (secondCard) {
          const firstCardRect = firstCard.getBoundingClientRect();
          const secondCardRect = secondCard.getBoundingClientRect();
          gap = secondCardRect.left - firstCardRect.right;
        }
        
        const totalCardWidth = cardWidth + gap;
        
        const containerWidth = wheelElement.parentElement?.clientWidth || 600;
        const cardHalfWidth = cardWidth / 2;
        const centerOffset = containerWidth / 2 - cardHalfWidth;
        
        // Use the fixed target index
        const targetIndex = targetIndexRef.current;
        
        if (targetIndex === -1 || targetIndex >= wheelAvatars.length) {
          console.error('Target index invalid:', targetIndex, 'wheelAvatars.length:', wheelAvatars.length);
          return;
        }
        
        // Verify target avatar is at correct position
        const targetAvatar = wheelAvatars[targetIndex];
        if (!targetAvatar || targetAvatar.id !== result.avatar.id) {
          console.error('Target avatar mismatch! Expected:', result.avatar.id, 'Found:', targetAvatar?.id);
          return;
        }
        
        // Calculate target position: index * totalCardWidth gives us the left edge of the card
        // We want the center of the card to align with the center of the container
        const targetPosition = targetIndex * totalCardWidth;
        
        // Start from position 0 (or small random offset for variety)
        const startPosition = Math.random() * totalCardWidth * 2; // Small random start
        const totalDistance = targetPosition - startPosition;
        
        // Animation parameters
        const duration = 3000; // 3 seconds total
        const startTime = performance.now();
        
        // Easing function (ease-out cubic)
        const easeOutCubic = (t) => {
          return 1 - Math.pow(1 - t, 3);
        };
        
        const animate = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1); // 0 to 1
          
          // Apply easing
          const easedProgress = easeOutCubic(progress);
          
          // Calculate current position
          const currentPosition = startPosition + totalDistance * easedProgress;
          
          // Update transform
          wheelElement.style.transform = `translateX(${centerOffset - currentPosition}px)`;
          
          if (progress < 1) {
            // Continue animation
            animationFrameId = requestAnimationFrame(animate);
          } else {
            // Animation complete - ensure exact position
            const finalTransform = centerOffset - targetPosition;
            wheelElement.style.transform = `translateX(${finalTransform}px)`;
            
            // Verify the target card is actually centered
            requestAnimationFrame(() => {
              const targetCard = wheelElement.children[targetIndex];
              if (targetCard) {
                const cardRect = targetCard.getBoundingClientRect();
                const containerRect = wheelElement.parentElement.getBoundingClientRect();
                const cardCenter = cardRect.left + cardRect.width / 2;
                const containerCenter = containerRect.left + containerRect.width / 2;
                const offset = cardCenter - containerCenter;
                
                // Fine-tune if needed
                if (Math.abs(offset) > 1) {
                  const currentTransform = parseFloat(wheelElement.style.transform.match(/translateX\(([^)]+)\)/)?.[1] || '0');
                  wheelElement.style.transform = `translateX(${currentTransform - offset}px)`;
                }
                
                // Double verify the avatar ID matches
                // Calculate which card is actually centered
                const wheelRect = wheelElement.getBoundingClientRect();
                const relativeCenter = containerCenter - wheelRect.left;
                const centeredCardIndex = Math.round(relativeCenter / totalCardWidth);
                
                // Verify which card is actually centered
                if (centeredCardIndex >= 0 && centeredCardIndex < wheelAvatars.length) {
                  const centeredAvatar = wheelAvatars[centeredCardIndex];
                  if (centeredAvatar.id !== result.avatar.id) {
                    console.error('Avatar mismatch! Expected at index', targetIndex, 'but centered at', centeredCardIndex);
                    console.error('Expected avatar:', result.avatar.id, 'Centered avatar:', centeredAvatar.id);
                    
                    // Force correct position - recalculate and set
                    const correctTargetPosition = targetIndex * totalCardWidth;
                    const correctTransform = centerOffset - correctTargetPosition;
                    wheelElement.style.transform = `translateX(${correctTransform}px)`;
                    
                    // Verify again after correction
                    requestAnimationFrame(() => {
                      const targetCard = wheelElement.children[targetIndex];
                      if (targetCard) {
                        const cardRect = targetCard.getBoundingClientRect();
                        const containerRect = wheelElement.parentElement.getBoundingClientRect();
                        const cardCenter = cardRect.left + cardRect.width / 2;
                        const containerCenter = containerRect.left + containerRect.width / 2;
                        const finalOffset = cardCenter - containerCenter;
                        
                        if (Math.abs(finalOffset) > 1) {
                          const currentTransform = parseFloat(wheelElement.style.transform.match(/translateX\(([^)]+)\)/)?.[1] || '0');
                          wheelElement.style.transform = `translateX(${currentTransform - finalOffset}px)`;
                        }
                      }
                    });
                  }
                }
              }
            });
            
            // Show revealing stage
            revealTimeout = setTimeout(() => {
              setAnimationStage('revealing');
              setRevealedAvatar(result.avatar);
              
              // Show complete stage
              completeTimeout = setTimeout(() => {
                setAnimationStage('complete');
                hasCompletedRef.current = true;
                if (onAnimationCompleteRef.current) {
                  onAnimationCompleteRef.current();
                }
              }, 800);
            }, 500);
          }
        };
        
        // Start animation
        animationFrameId = requestAnimationFrame(animate);
      };
      
      // Start animation after a short delay to ensure DOM is ready
      const initTimeout = setTimeout(() => {
        startAnimation();
      }, 100);
      
      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        if (revealTimeout) {
          clearTimeout(revealTimeout);
        }
        if (completeTimeout) {
          clearTimeout(completeTimeout);
        }
        clearTimeout(initTimeout);
      };
    } else if (!open) {
      // Reset when dialog closes
      setAnimationStage('spinning');
      setRevealedAvatar(null);
      setWheelAvatars([]);
      if (wheelRef.current) {
        wheelRef.current.style.transform = '';
      }
      hasCompletedRef.current = false;
    }
  }, [open, result, wheelAvatars]);

  const rarityColors = {
    R: { bg: 'rgba(33, 150, 243, 0.9)', glow: 'rgba(33, 150, 243, 0.5)' },
    SR: { bg: 'rgba(156, 39, 176, 0.9)', glow: 'rgba(156, 39, 176, 0.5)' },
    SSR: { bg: 'rgba(255, 152, 0, 0.9)', glow: 'rgba(255, 152, 0, 0.5)' }
  };

  // Use neutral color during spinning, actual color after revealing
  const neutralColor = { bg: 'rgba(128, 128, 128, 0.9)', glow: 'rgba(128, 128, 128, 0.5)' };
  const currentColor = (animationStage === 'spinning') 
    ? neutralColor 
    : (result ? rarityColors[result.rarity] : rarityColors.R);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)', // Increased opacity, removed backdropFilter for performance
          boxShadow: 'none',
          overflow: 'visible'
        }
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.8)' // Removed backdropFilter for performance
        }
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'visible', position: 'relative' }}>
        {/* Close button in top right corner */}
        {animationStage === 'complete' && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            position: 'relative'
          }}
        >
          {/* Background glow effect */}
          <Box
            sx={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${currentColor.glow}, transparent)`,
              filter: 'blur(40px)',
              zIndex: 0,
              animation: animationStage === 'revealing' 
                ? 'pulse 0.5s ease-in-out' 
                : animationStage === 'spinning'
                ? 'glow 2s ease-in-out infinite'
                : 'none',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
                '50%': { transform: 'scale(1.5)', opacity: 0.8 }
              },
              '@keyframes glow': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.6 }
              }
            }}
          />

          {/* Spinning wheel */}
          {animationStage === 'spinning' && wheelAvatars.length > 0 && (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: '700px',
                overflow: 'hidden',
                py: 6,
                px: 2
              }}
            >
              {/* Center indicator - top and bottom borders */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '140px',
                  height: '140px',
                  border: `4px solid ${currentColor.glow}`,
                  borderRadius: 2,
                  boxShadow: `0 0 40px ${currentColor.glow}, inset 0 0 40px ${currentColor.glow}`,
                  zIndex: 3,
                  pointerEvents: 'none',
                  background: 'rgba(0, 0, 0, 0.6)', // Removed backdropFilter for performance
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${currentColor.glow}, transparent)`,
                    zIndex: -1,
                    filter: 'blur(10px)'
                  }
                }}
              />
              
              {/* Top and bottom fade gradients */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '100px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)',
                  zIndex: 2,
                  pointerEvents: 'none'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '100px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                  zIndex: 2,
                  pointerEvents: 'none'
                }}
              />
              
              {/* Wheel container */}
              <Box
                ref={wheelRef}
                sx={{
                  display: 'flex',
                  gap: 2,
                  willChange: 'transform',
                  transform: 'translateX(calc(50% - 70px))',
                  // Use GPU acceleration
                  transformStyle: 'preserve-3d',
                  backfaceVisibility: 'hidden'
                }}
              >
                {wheelAvatars.map((avatar, index) => (
                  <Box
                    key={`${avatar.id}-${index}`}
                    sx={{
                      flexShrink: 0,
                      width: '120px',
                      height: '120px',
                      opacity: 0.5,
                      // Disable transitions during animation for better performance
                      transition: 'none',
                      // GPU acceleration
                      transform: 'translateZ(0)',
                      willChange: 'transform, opacity'
                    }}
                  >
                    <AvatarCard
                      avatar={avatar}
                      owned={true}
                      selected={false}
                      hideName={true}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Revealing card */}
          {animationStage === 'revealing' && revealedAvatar && (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                animation: 'reveal 0.8s ease-out',
                '@keyframes reveal': {
                  '0%': { transform: 'scale(0.5) rotateY(180deg)', opacity: 0 },
                  '100%': { transform: 'scale(1) rotateY(0deg)', opacity: 1 }
                }
              }}
            >
              <Box
                sx={{
                  width: '250px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    animation: 'pulseCard 0.6s ease-in-out',
                    '@keyframes pulseCard': {
                      '0%, 100%': { 
                        transform: 'scale(1)',
                        filter: `drop-shadow(0 0 20px ${currentColor.glow})`
                      },
                      '50%': { 
                        transform: 'scale(1.1)',
                        filter: `drop-shadow(0 0 60px ${currentColor.glow})`
                      }
                    }
                  }}
                >
                  <AvatarCard
                    avatar={revealedAvatar}
                    owned={true}
                    selected={false}
                    hideName={false}
                  />
                </Box>
                
              </Box>
            </Box>
          )}

          {/* Complete stage */}
          {animationStage === 'complete' && revealedAvatar && (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                animation: 'fadeIn 0.5s ease-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 }
                }
              }}
            >
              <Box
                sx={{
                  width: '250px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <AvatarCard
                  avatar={revealedAvatar}
                  owned={true}
                  selected={false}
                  hideName={false}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default GachaAnimation;
